import { motion } from "framer-motion";
import { CalendarCheck } from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { ProductMockup } from "@/components/landing/ProductMockup";
import { IntegrationsSlider } from "@/components/landing/IntegrationsSlider";
import { MobileAppPreview } from "@/components/landing/MobileAppPreview";

/* "Meet Clinic OS" — the brand intro section that sits right after the
   problem-focused hero. Visitors first feel the pain, then meet the solution. */
export const MeetClinicOS = () => {
  const openDemo = useDemo();
  return (
    <section
      id="meet-clinic-os"
      data-testid="meet-clinic-os-section"
      className="relative overflow-hidden pt-12 pb-20 sm:pt-16 sm:pb-28"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 text-center sm:px-8 lg:px-12">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
          The solution
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.05 }}
          className="mx-auto mt-5 max-w-5xl font-display font-black leading-[1] tracking-[-0.03em]"
        >
          <span className="block text-[3rem] sm:text-7xl lg:text-[5.5rem]">
            Meet <span className="text-gradient">Clinic&nbsp;OS</span>
          </span>
          <span className="mt-3 block text-[1.5rem] leading-[1.05] tracking-[-0.02em] text-foreground/90 sm:mt-4 sm:text-[2.25rem] lg:text-[2.75rem]">
            Operating System for <span className="text-gradient">Clinic</span>
          </span>
          <span className="mt-2 block text-[1.125rem] font-bold leading-[1.1] tracking-[-0.015em] text-foreground/70 sm:mt-3 sm:text-2xl lg:text-[1.625rem]">
            One Platform, One Login
          </span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-7 flex flex-col items-center justify-center gap-4"
        >
          <button
            data-testid="meet-book-demo"
            onClick={openDemo}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-foreground px-8 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 hover:opacity-90 sm:h-14 sm:text-base"
          >
            <CalendarCheck className="h-4 w-4" /> Book a Demo
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-6 hidden md:mt-8 md:block"
        >
          <ProductMockup />
        </motion.div>

        {/* Mobile-only phone mockup — same purpose as ProductMockup, sized for mobile */}
        <MobileAppPreview />

        <div className="hidden md:block">
          <IntegrationsSlider />
        </div>
      </div>
    </section>
  );
};
